import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';
import { RateCardLibrary } from '../components/RateCardLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default surcharges
const DEFAULT_SURCHARGES = [
  { description: "ERP & Parking Charges", rate: "At Cost" },
  { description: "Any Other Charges", rate: "As Per Receipt" },
  { description: "Night 1900hrs - 0000hrs", rate: "Rate x1.5 Per Hr" },
  { description: "Midnight 0000 Hrs - 0800 Hrs", rate: "Rate x1.5 Per Hr" },
  { description: "Sunday & Public Holidays 24 Hrs", rate: "Rate x2 Per Hr" },
];

export const CreateQuotation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCash = searchParams.get('cash') === '1';
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [formData, setFormData] = useState({
    // Company Details
    company_name: '',
    company_contact: '',
    company_email: '',
    company_address: '',
    // Customer Details (Contact Person)
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    site_contact_name: '',
    site_contact_phone: '',
    validity_days: 30,
    notes: ''
  });
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, hours: '', unit_price: 0, amount: 0 }
  ]);
  const [surcharges, setSurcharges] = useState([...DEFAULT_SURCHARGES]);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing`);
      setPricing(response.data);
    } catch (error) {
      toast.error('Failed to load pricing');
      console.error(error);
    }
  };

  // Surcharge functions
  const addSurcharge = () => {
    setSurcharges([...surcharges, { description: '', rate: '' }]);
  };

  const removeSurcharge = (index) => {
    setSurcharges(surcharges.filter((_, i) => i !== index));
  };

  const updateSurcharge = (index, field, value) => {
    const updated = [...surcharges];
    updated[index][field] = value;
    setSurcharges(updated);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, hours: '', unit_price: 0, amount: 0 }]);
  };

  const addRateFromCard = (item) => {
    const newItem = { ...item, hours: '' };
    setLineItems((prev) => {
      if (prev.length === 1 && !prev[0].description && !(prev[0].amount > 0)) return [newItem];
      return [...prev, newItem];
    });
  };

  const removeLineItem = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Auto-calculate amount: qty × hours × unit_price (or qty × unit_price if hours is empty)
    if (field === 'quantity' || field === 'hours' || field === 'unit_price') {
      const qty = parseFloat(updated[index].quantity || 0);
      const hours = parseFloat(updated[index].hours || 0);
      const price = parseFloat(updated[index].unit_price || 0);
      updated[index].amount = hours > 0 ? qty * hours * price : qty * price;
    }
    
    setLineItems(updated);
  };

  const selectService = (index, serviceId, rateType) => {
    if (!pricing) return;
    
    let service = null;
    const allServices = [
      ...pricing.services.lorry_cranes,
      ...pricing.services.trailers,
      ...pricing.services.tailgate
    ];
    
    service = allServices.find(s => s.id === serviceId);
    if (serviceId === 'manpower') {
      service = pricing.services.manpower;
    }
    if (serviceId === 'warehouse_storage') {
      service = pricing.services.warehouse_storage;
    }
    if (serviceId === 'handling_fee') {
      service = pricing.services.handling_fee;
    }
    
    if (!service) return;
    
    const updated = [...lineItems];
    let unitPrice = 0;
    let description = service.name;
    
    if (rateType === 'hourly' && service.hourly_rate) {
      unitPrice = service.hourly_rate;
      description += ' - Hourly Rate';
    } else if (rateType === 'half_day' && service.half_day_rate) {
      unitPrice = service.half_day_rate;
      description += ' - Half Day';
    } else if (rateType === 'per_trip' && service.per_trip_rate) {
      unitPrice = service.per_trip_rate;
      description += ' - Per Trip';
    } else if (rateType === 'per_day' && service.per_day_rate) {
      unitPrice = service.per_day_rate;
      description += ' - Per Day';
    } else if (rateType === 'as_quoted') {
      unitPrice = 0; // Admin enters manually
      description += ' - As Quoted';
    }
    
    updated[index].description = description;
    updated[index].unit_price = unitPrice;
    const qty = parseFloat(updated[index].quantity || 0);
    const hours = parseFloat(updated[index].hours || 0);
    updated[index].amount = hours > 0 ? qty * hours * unitPrice : qty * unitPrice;
    
    setLineItems(updated);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const quotationData = {
        ...formData,
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          hours: item.hours !== '' && item.hours != null ? parseFloat(item.hours) : null,
          unit_price: parseFloat(item.unit_price),
          amount: parseFloat(item.amount)
        })),
        surcharges: surcharges.filter(s => s.description && s.rate),
        is_cash: isCash
      };

      const response = await axios.post(`${BACKEND_URL}/api/quotations-billing/`, quotationData);

      if (response.data.success) {
        const msg = response.data.message;
        toast.success(typeof msg === 'string' ? msg : 'Quotation created successfully');
        navigate('/admin/billing');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create quotation'));
    } finally {
      setLoading(false);
    }
  };

  const subtotal = calculateSubtotal();

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                {isCash ? 'Create Cash Quotation' : 'Create Quotation'}
                {isCash && <span className="text-sm font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-3 py-1" data-testid="cash-badge">CASH</span>}
              </h1>
              <p className="text-gray-600 mt-1">{isCash ? 'CQT numbering — payment in cash upon completion' : 'Generate a new quotation for customer'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Company & Customer Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Company Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Company Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name *</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        placeholder="ABC Construction Pte Ltd"
                        required
                      />
                    </div>
                    <div>
                      <Label>Company Contact *</Label>
                      <Input
                        type="tel"
                        value={formData.company_contact}
                        onChange={(e) => setFormData({...formData, company_contact: e.target.value})}
                        placeholder="6512345678"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Company Email *</Label>
                    <Input
                      type="email"
                      value={formData.company_email}
                      onChange={(e) => setFormData({...formData, company_email: e.target.value})}
                      placeholder="info@company.com"
                      required
                    />
                  </div>
                  <div>
                    <Label>Company Address</Label>
                    <Textarea
                      value={formData.company_address}
                      onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                      placeholder="123 Main Street, Singapore 123456"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Customer Details (Contact Person) */}
              <Card>
                <CardHeader>
                  <CardTitle>Customer Details (Contact Person)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Name *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <Label>Contact Phone *</Label>
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                        placeholder="91234567"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Site Contact Name</Label>
                      <Input
                        value={formData.site_contact_name}
                        onChange={(e) => setFormData({...formData, site_contact_name: e.target.value})}
                        placeholder="e.g., Mr Tan"
                        data-testid="qt-site-contact-name"
                      />
                    </div>
                    <div>
                      <Label>Site Contact Phone</Label>
                      <Input
                        value={formData.site_contact_phone}
                        onChange={(e) => setFormData({...formData, site_contact_phone: e.target.value})}
                        placeholder="e.g., 9123 4567"
                        data-testid="qt-site-contact-phone"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Line Items</CardTitle>
                    <Button type="button" onClick={addLineItem} variant="outline">
                      <Plus size={16} className="mr-2" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <RateCardLibrary onAdd={addRateFromCard} />
                  <div className="space-y-4">
                    {lineItems.map((item, index) => (
                      <div key={index} className="border p-4 rounded-lg space-y-4">
                        <div className="flex justify-between items-start">
                          <p className="font-semibold">Item {index + 1}</p>
                          {lineItems.length > 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>

                        {pricing && (
                          <div>
                            <Label>Quick Select Service</Label>
                            <div className="mt-2">
                              <select 
                                className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const [serviceId, rateType] = e.target.value.split('|');
                                    selectService(index, serviceId, rateType);
                                    e.target.value = '';
                                  }
                                }}
                                defaultValue=""
                              >
                                <option value="" disabled>Select service...</option>
                                <optgroup label="Lorry Cranes">
                                  {pricing.services.lorry_cranes.map(crane => (
                                    <React.Fragment key={crane.id}>
                                      {crane.hourly_rate && (
                                        <option value={`${crane.id}|hourly`}>
                                          {crane.name} - Hourly
                                        </option>
                                      )}
                                      {crane.per_trip_rate && (
                                        <option value={`${crane.id}|per_trip`}>
                                          {crane.name} - Per Trip
                                        </option>
                                      )}
                                      {crane.half_day_rate && (
                                        <option value={`${crane.id}|half_day`}>
                                          {crane.name} - Half Day
                                        </option>
                                      )}
                                      {crane.per_day_rate && (
                                        <option value={`${crane.id}|per_day`}>
                                          {crane.name} - Per Day
                                        </option>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </optgroup>
                                <optgroup label="Trailers">
                                  {pricing.services.trailers.map(trailer => (
                                    <React.Fragment key={trailer.id}>
                                      {trailer.hourly_rate && (
                                        <option value={`${trailer.id}|hourly`}>
                                          {trailer.name} - Hourly
                                        </option>
                                      )}
                                      {trailer.half_day_rate && (
                                        <option value={`${trailer.id}|half_day`}>
                                          {trailer.name} - Half Day
                                        </option>
                                      )}
                                      {trailer.per_day_rate && (
                                        <option value={`${trailer.id}|per_day`}>
                                          {trailer.name} - Per Day
                                        </option>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </optgroup>
                                <optgroup label="Tailgate">
                                  {pricing.services.tailgate.map(tg => (
                                    <React.Fragment key={tg.id}>
                                      {tg.hourly_rate && (
                                        <option value={`${tg.id}|hourly`}>
                                          {tg.name} - Hourly
                                        </option>
                                      )}
                                      {tg.half_day_rate && (
                                        <option value={`${tg.id}|half_day`}>
                                          {tg.name} - Half Day
                                        </option>
                                      )}
                                      {tg.per_day_rate && (
                                        <option value={`${tg.id}|per_day`}>
                                          {tg.name} - Per Day
                                        </option>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </optgroup>
                                <optgroup label="Other Services">
                                  <option value="manpower|per_day">
                                    Manpower - Per Day
                                  </option>
                                  <option value="warehouse_storage|as_quoted">
                                    Warehouse Storage - As Quoted
                                  </option>
                                  <option value="handling_fee|as_quoted">
                                    Handling Fee (Loading/Unloading) - As Quoted
                                  </option>
                                </optgroup>
                              </select>
                            </div>
                          </div>
                        )}

                        <div>
                          <Label>Description *</Label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            required
                            data-testid={`line-item-description-${index}`}
                          />
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Hours</Label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.hours || ''}
                              onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                              placeholder="e.g. 3"
                              data-testid="qt-line-hours"
                            />
                          </div>
                          <div>
                            <Label>Unit Price ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Amount ($)</Label>
                            <Input
                              type="number"
                              value={item.amount.toFixed(2)}
                              readOnly
                              className="bg-gray-50"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Surcharges Section */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Additional Surcharges</CardTitle>
                    <Button type="button" onClick={addSurcharge} variant="outline" size="sm">
                      <Plus size={16} className="mr-2" />
                      Add Surcharge
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {surcharges.map((surcharge, index) => (
                      <div key={index} className="flex gap-3 items-center">
                        <div className="w-8 text-center text-gray-500 font-medium">{index + 1}</div>
                        <div className="flex-1">
                          <Input
                            value={surcharge.description}
                            onChange={(e) => updateSurcharge(index, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </div>
                        <div className="w-40">
                          <Input
                            value={surcharge.rate}
                            onChange={(e) => updateSurcharge(index, 'rate', e.target.value)}
                            placeholder="Rate"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSurcharge(index)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Validity (Days)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.validity_days}
                      onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={4}
                      placeholder="Additional notes or terms..."
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Summary */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Quotation Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">GST (0%):</span>
                      <span className="font-semibold">$0.00</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-xl text-blue-600">${subtotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Creating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save size={20} />
                          Create Quotation
                        </span>
                      )}
                    </Button>
                  </div>

                  <div className="text-xs text-gray-500 mt-4">
                    <p className="mb-2">Payment Terms:</p>
                    <p className="font-medium">{isCash ? 'CASH — payment upon completion' : (pricing?.payment_terms || '100% Payment Upon Confirmation')}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
